<template>
	<header-block :title="mortgageHeader" bgcolor="white" border="0"></header-block>
	<CustomerLoadWait :customer="customer && creditProviders"/>
	<div class="progress-container" v-if="sections">
		<v-progress-linear :model-value="progressValue" rounded></v-progress-linear>
	</div>
	<transition>
		<div v-if="customer && creditProviders && (policy || !this.$route.params.id)">
			<div class="personalDetails" v-if="sections">
				<InputSections ref="inputSections" :sections="sections" theme="light"/>
				<div v-if="validationText" class="well">
					<p>{{ validationText }}</p>
				</div>
				<div class="submitControls">
					<v-row>
						<v-col cols="5" v-if="currentPageNumber > 0"><MGButton small="true" :outline="true" @click="_inputControl_GoBack()" text="Back" /></v-col>
						<v-col><MGButton :small="currentPageNumber > 0" :alt="true" text="Next" :arrow="true" @click="_inputControl_GoNext(addMortgage, _preValidate)"/></v-col>
					</v-row>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import Helpers from '../mixins/Helpers';
import Services from '../mixins/Services';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import InputSections from './elements/InputSections.vue';
import MGButton from './elements/MGButton.vue';

export default {
	mixins: [Services, Helpers],
	components: {HeaderBlock, InputSections, MGButton, CustomerLoadWait},
	data() {
		return {
			validationText: null,
			sectionNo: 0,
			currentPageNumber: 0,
			sections: []
		}
	},
	computed: {
		mortgageHeader() {
			var verb = this.$route.params.id ? 'Edit' : 'Add';
			if (!this.sections || !this.sections[0] || !this.sections[0].fields || !this.sections[0].fields[0] || !this.sections[0].fields[0].value) {
				return verb + " mortgage details";
			}
			var title = verb + " " + this.sections[0].fields[0].value + " Mortgage details";
			return title;
		},
		progressValue() {
			var insuranceType = this._findFieldValue('Type');
			var totalPages = this.sectionsAvailableCount(insuranceType);
			if (totalPages === 0) return 0;
			return ((this.currentPageNumber + 1) / totalPages) * 100;
		},
	},
	methods: {
		onLoad: async function() {
			this._getAiopSection('AddMortgagePageSections.json', () => {
				this.sections = this.sectionJson;
				if (this.$route.params.id) {
					this._getMortgage(this.$route.params.id, (policy) => {
						this.policy = policy;
						this.populateFields();
					});
				}
				this._getCustomer();
				this._getCreditProviders((res) => {
					res = res.filter(item => item.mortgage === true)
					.filter((item, index, self) => 
							index === self.findIndex((t) => t.name === item.name)
						);
					var providerField = this._findField('Provider');
					if (providerField) {
						providerField.items = res.map(x => {
							return x.name.replace(/\w\S*/g, (txt) => {
								return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
							});
						});
					}
				});
			});
		},
		populateFields() {
			const fieldMappings = {
				'Ownership': this.policy.account.jointStatus === 2 ? 'Joint' : 'Sole',
				'Provider': this.policy.account.provider.name,
				'Account number': this.policy.account.reference,
				'Balance': this.policy.account.balances[this.policy.account.balances.length - 1].amount,
				'Monthly Repayment': this.policy.monthlyRepaymentAmount,
				'Date opened': this._dateToString(this.policy.account.creditCreateDate),
				'Term (Years)': this.policy.account.termMonths / 12,
				'Type': this.policy.interestRateType === 0 ? 'Fixed' : this.policy.interestRateType === 1 ? 'Standard Variable' : this.policy.interestRateType === 2 ? 'Discount Tracker' : null,
				'Deal end date': this._dateToString(this.policy.dealEndDate),
				'Deal rate': this.policy.dealRate,
				'Interest rate when deal ends': this.policy.rate,
				'Interest rate': this.policy.rate,
				'Address': this.policy.address
			};

			this._loadFieldsFromObject(fieldMappings);
		},
		addMortgage() {
			const creditType = {
				name: this._findFieldValue('Type') || null
			};
			const provider = {
				name: this._findFieldValue('Provider') || null,
			};
			const balance = {
				amount: this._findFieldValue('Balance') || null,
			}
			const balances = [balance];
			var addressObject = this._findFieldValue('Address') || null;
			const addressInsured = {
				line1: addressObject?.line1 || null,
				line2: addressObject?.line2 || null,
				line3: addressObject?.line3 || null,
				town: addressObject?.town || null,
				postCode: addressObject?.postCode || null,
			};

			const creditAccount = ({
				address: addressInsured,
				account: {
					provider: provider,
					creditType: creditType,
					reference: this._findFieldValue('Account number') || null,
					balances: balances,
					balance: balance,
					creditCreateDate: this._stringToDate(this._findFieldValue('Date opened')) || null,
					termMonths: this._findFieldValue('Term (Years)') * 12 || null,
					jointStatus: this._findFieldValue('Ownership') === 'Joint' ? 2 : this._findFieldValue('Ownership') === 'Sole' ? 1 : null,
				},
				monthlyRepaymentAmount: this._findFieldValue('Monthly Repayment') || null,
				dealEndDate: this._stringToDate(this._findFieldValue('Deal end date')) || null,
				dealRate: this._findFieldValue('Deal rate') || null,
				rate: this._findFieldValue('Interest rate when deal ends') || this._findFieldValue('Interest rate') || null,
				interestRateType: this._findFieldValue('Type') === 'Fixed' ? 0 : this._findFieldValue('Type') === 'Standard Variable' ? 1 : this._findFieldValue('Type') === 'Discount Tracker' ? 2 : null,
				termMonths: this._findFieldValue('Term') || null,
				creditCreateDate:  this._stringToDate(this._findFieldValue('Start date')) || null,
			});

			if (this.$route.params.id) {
				creditAccount.id = this.$route.params.id;
			}

			this._removeNullProperties(creditAccount);

			const handleResponse = (res) => {
				if (res) {
					if (this.$route.params.id) {
						this._getMortgage(this.$route.params.id, /*onSuccess*/ null, /*refreshCache*/ true);
					}
					this._getMortgageInfo(/*onSuccess*/ null, /*refreshCache*/ true);
					this.$router.push({ path: '/policysuccess', query: { type: 'mortgage' } });
				}
			};

			if (!this.$route.params.id) {
				this._addMortgageAccount(creditAccount, handleResponse);
			} else {
				this._updateMortgageAccount(creditAccount, handleResponse);
			}
		},
		sectionsAvailableCount(insuranceType) {
			var sectionsNoRule = this.sections.filter(section => !section.rule);
			var sectionsMatchingRule = this.sections.filter(section => section.rule && section.rule.includes(insuranceType));
			return sectionsNoRule.length + sectionsMatchingRule.length;
		},
	},
	mounted: function () {
		this.onLoad();
	},
}
</script>

<style scoped>


:deep(.check .v-label) {
	color: white !important;
}

.well {
	text-align: center;
	background-color: #3328470D;
}

.progress-container {
	display: flex;
	justify-content: center;
	margin: 20px 0;
}

:deep(.v-progress-linear__background) {
  background-color: var(--border-color) !important;
}

.submitControls {
	margin-top: 20px;
	padding-bottom: 20px;
}

.personalDetails {
	overscroll-behavior: contain;
}
</style>
